/**
 * English message catalogue — the source of truth for keys and placeholders.
 * `as const` keeps each message a literal so the accessor can derive its
 * variables and reject unknown keys (ADR-0042).
 */
export const en = {
  title: "egalregal",
  homeHint: "Start typing to search your storage",
  searchPlaceholder: "Search boxes, racks, rooms…",
  // The field's accessible name; the placeholder is only a visual hint
  // (shell-redesign 12, spec §10).
  search: "Search",
  searchMode: "Search engine",
  searchModeKeyword: "Keyword",
  searchModeSemantic: "Semantic",
  searchRecent: "Recently opened",
  clearSearch: "Clear search",
  scopeAll: "All",
  scopeContainers: "Containers",
  scopeItems: "Items",
  scopePhotos: "Photos",
  browse: "Browse",
  add: "Add",
  home: "Home",
  // Landmark and crumb labels exposed to assistive tech (spec §10).
  primaryNav: "Primary",
  breadcrumb: "Breadcrumb",
  switchLanguage: "Switch language to {language}",
  online: "Online",
  offline: "Offline",
  login: "Login",
  username: "Username",
  password: "Password",
  loginFailed: "Login failed — check your username and password.",
  oauth2Failed: "SSO login failed — try again or use your password.",
  loginWithProvider: "Login with {provider}",
  localAuthDisabled: "Password login is disabled — use your identity provider.",
  demoBanner: "Read-only demo — run egalregal locally to make changes.",
  or: "or",
  loading: "Loading…",
  logout: "Logout",
  homeTitle: "Your storage",
  noContainers: "No containers yet — add your first box.",
  browseHint: "Browse by location is coming next.",
  addTitle: "Add container",
  editTitle: "Edit container",
  formName: "Name",
  formDescription: "Description",
  formOwner: "Owner",
  ownerSuggestions: "Recent owners",
  formSpace: "Space",
  formKind: "Kind",
  formTags: "Tags",
  tagsHint: "Recent tags offered first; type to add your own",
  save: "Save",
  cancel: "Cancel",
  close: "Close",
  edit: "Edit",
  delete: "Delete",
  back: "Back",
  backToSearch: "Back to search results",
  backToScan: "Back to scan",
  backToStorage: "Back to storage",
  allStorage: "All storage",
  unsaved: "Unsaved changes",
  discardTitle: "Discard changes?",
  discardBody: "This container has unsaved changes.",
  keepEditing: "Keep editing",
  discard: "Discard",
  collapseNav: "Collapse navigation",
  expandNav: "Expand navigation",
  offlineBanner: "Offline — showing cached data",
  next: "Next",
  menu: "Menu",
  saved: "Saved",
  created: "Created",
  createAsChild: "New sub-container of {parent}",
  createLinkError:
    "Created at the top level, but it could not be added under {parent}: {error}",
  // Quick-add current location (ticket 12): the transient parent of new containers.
  quickAddTitle: "Quick-add",
  quickAddHint: "Quick-add: new containers can be added under one location.",
  quickAddSet: "Set current location",
  quickAddUnder: "New containers will be added under {path}",
  quickAddOverride:
    "This box goes under {parent} (one-off). Next boxes go under {path}.",
  quickAddChange: "Change location",
  quickAddStop: "Stop auto-linking",
  quickAddPickTitle: "Choose current location",
  saveError: "Could not save: {error}",
  saveEntryError: "Saved, but the catalog entry could not be changed: {error}",
  captureCamera: "Camera",
  captureRecord: "Record",
  captureFile: "File",
  captureTakePhoto: "Take photo",
  captureRetake: "Retake",
  captureKeep: "Keep",
  captureStop: "Stop",
  capturePause: "Pause",
  captureResume: "Resume",
  capturePreview: "Captured photo",
  captureCameraError: "Camera unavailable — use File instead.",
  captureMicError: "Microphone unavailable — use File instead.",
  cameraSaveError: "Could not save the photos — is the server reachable?",
  files: "Photos & memos",
  record: "Record",
  noTranscript: "No transcript yet.",
  // Container-media 02: the View menu's sub-container media controls and the
  // empty note shown when no descendant media matches the kind filter.
  includeSubContainerMedia: "Include sub-container media",
  containerKinds: "Container kinds",
  noSubContainerMedia: "No matching media in sub-containers.",
  transcriptHeading: "Transcript",
  removeFile: "Remove",
  removeFileFailed: "Could not remove the file.",
  browseTitle: "Browse by location",
  containersTitle: "Containers",
  move: "Move",
  link: "Link",
  unlink: "Unlink",
  linkChildTitle: "Link this container under…",
  details: "Details",
  // Merged container view (container-view 01): one menu line per section.
  more: "More",
  view: "View",
  tree: "Tree",
  menuContainer: "Container",
  hideDetails: "Hide details",
  showDetails: "Show details",
  addSubContainer: "Add sub-container",
  selectContainers: "Select containers",
  linkFailed: "Could not link the container.",
  unlinkFailed: "Could not unlink the container.",
  moveTitle: "Move container to…",
  moveToRoot: "Top level",
  movePickEntry: "Choose the destination catalog entry",
  moveNoEntries: "No catalog entries available.",
  movePickParent: "Place inside {entry}",
  moveNoContainers: "No containers in this entry.",
  moveTransferTitle: "Transfer ownership",
  moveTransferBody: "You are about to move this container and its entire subtree to {entry}.",
  moveTransferWarning: "A move-back is the only way home; item placements will be dropped.",
  moveTransferConfirm: "Transfer ownership",
  // Container bulk mode and its destination dialog (bulk-move 05)
  bulkMoveTitle: "Move child containers to…",
  bulkSelected: "{count} selected",
  bulkMoveAction: "Move…",
  bulkSelectAll: "Select all",
  bulkCapHint: "Only the first {count} rows can be selected.",
  bulkPickEntry:
    "Own entries and shared entries you can write to. A shared entry in another home is an ownership transfer.",
  bulkDestinationCurrent: "Current entry",
  bulkTransferChip: "Transfer",
  bulkTargetSearch: "Search containers in {entry}",
  bulkTargetNoMatches: "No containers match “{query}”.",
  bulkMoveNoOp: "These containers are already at this destination.",
  bulkMoveConfirm: "Move {count}",
  bulkTransferBody:
    "Transfer {count} containers and their entire subtrees as roots of {entry}.",
  bulkTransferWarning:
    "A move-back is the only way home; item placements inside the transferred subtrees will be dropped.",
  bulkTransferConfirm: "Transfer {count}",
  bulkMoveSuccess: "Moved {count} containers to {destination}.",
  bulkMoveSuccessDropped:
    "Moved {count} containers to {destination}; {dropped} item placements dropped.",
  bulkMovePartial: "Moved {moved} of {total}; {failed} failed: {reasons}",
  bulkMovePartialDropped: "{dropped} item placements dropped.",
  bulkMoveFailed: "The bulk move failed.",
  bulkMoveRowsChanged: "Some selected containers changed; reload and retry.",
  bulkDestinationLoadFailed: "Could not load the destination entry.",
  // Item bulk mode and its destination dialog (bulk-move 06)
  itemBulkMoveTitle: "Move item placements to…",
  itemBulkPickEntry:
    "Choose a catalog entry in the same home. Quantities are kept and merge into an existing placement.",
  itemBulkPickContainer: "Choose a container in {entry}",
  itemBulkSourceNode: "Source",
  itemBulkMoveConfirm: "Move {count} items",
  itemBulkMoveSuccess: "Moved {count} items to {destination}.",
  itemBulkMoveFailed: "The item move failed.",
  movedToEntry: "Moved to {entry}",
  movedDroppedPlacements: "Moved; {count} item placements dropped.",
  hereEmpty: "Nothing here yet",
  topLevel: "Top level",
  searchResults: "Search results",
  noMatches: "No matches found.",
  trash: "Trash",
  trashEmpty: "Trash is empty.",
  restore: "Restore",
  purge: "Purge",
  purgeConfirmTitle: "Purge permanently?",
  purgeConfirmBody: "This permanently deletes the container and cannot be undone.",
  deleteConfirmTitle: "Delete this container?",
  deleteConfirmBody: "It moves to the trash and can be restored.",
  deleteConfirmChildren: "This container has {count} child containers. They will be unlinked, not deleted.",
  deleteSectionTitle: "Delete container",
  deleteError: "Could not delete the container: {error}",
  deletedAt: "Deleted",
  print: "Print",
  printTitle: "QR label sheets",
  printLabels: "Print labels ({count})",
  printSize: "Grid size",
  printColumns: "Columns",
  printRows: "Rows",
  scan: "Scan",
  scanTitle: "Scan a code",
  scanCameraError: "Camera unavailable — type the container ID below.",
  scanCameraErrorBarcode: "Camera unavailable — enter the barcode below.",
  scanCameraUnavailable: "Camera unavailable",
  scanScanning: "Scanning",
  scanAim: "Aim camera",
  scanTapToAim: "Tap the viewfinder to aim",
  scanAimPersisted: "Display choice is persisted · closes on the next scan",
  scanCloseAim: "Close aiming view",
  scanDisplayCover: "Cover",
  scanDisplayContain: "Full frame",
  scanDisplayScanArea: "Scan area",
  scanViewItem: "View item",
  scanNewLabel: "New label {id}",
  scanCopyPayload: "Copy",
  scanOpenLink: "Open link",
  scanPayloadCopied: "Copied to clipboard.",
  scanFallbackLabel: "Container ID (UUID)",
  scanNotFound: "Not found — check the ID and try again.",
  scanToLink: "Scan to link",
  scanLinkHint: "The container you are viewing will be linked as a child of the scanned container.",
  scanLinkFailed: "Linking failed — the scanned container is inside this container or cannot be used as its parent.",
  linkedAsChild: '"{child}" is now inside "{parent}".',
  scanSubmit: "Open",
  scanEmpty: "Scan a label to see its container here.",
  scanCreateNew: "Create container",
  scanCreateItem: "Create item",
  scanUnknownLabel: "Label {id} is not in use yet — create it as a container or as an item.",
  scanUnsupportedFormat: "Unsupported format — only plain UUID labels are supported right now.",
  scanOfflineUncached: "You're offline and this code is not in the local cache.",
  scanRetry: "Try again",
  // Payload view and safe actions (results-06)
  scanPayloadUrl: "URL",
  scanPayloadWifi: "Wi-Fi network",
  scanPayloadText: "Text",
  scanCopyPassword: "Copy password",
  scanPayloadPasswordCopied: "Password copied to clipboard.",
  scanPayloadCopyFailed: "Could not copy — select the text and copy it manually.",
  scanPayloadTruncated:
    "Stored content is capped at {limit} characters — only the saved part can be copied or shown.",
  scanPayloadShowFull: "Show full content",
  scanPayloadShowLess: "Show less",
  scanPayloadTextOnly: "Text only — never executed or loaded.",
  scanPayloadOpenTitle: "Open this link?",
  scanPayloadOpenBody:
    "This opens the destination in a new browser tab. Only continue if you trust where the code came from.",
  scanPayloadOpenConfirm: "Open link",
  scanPayloadSsid: "SSID",
  scanPayloadSecurity: "Security",
  scanPayloadOpenNetwork: "Open",
  scanPayloadHidden: "Hidden network",
  // Unknown product barcode (results-02)
  scanProductAdd: "Add to my items",
  scanProductHint: "Provider preview — not in your collection yet. Adding creates an unplaced item.",
  scanProductLooking: "Looking up barcode…",
  scanProductMiss: "No provider knows barcode {barcode} — add the details yourself.",
  scanProductOffline: "Offline — the barcode lookup is unavailable. Add the details yourself.",
  scanProductError: "Barcode lookup failed — add the details yourself.",
  scanProductAdded: "Added {name} to your items.",
  scanProductCreateFailed: "Could not create the item. Nothing was saved.",
  // Scan history overlay (results-05)
  scanManualOpen: "Manual input",
  scanHistoryOpen: "Show scan history",
  scanHistoryTitle: "Scan history",
  scanHistoryClose: "Close scan history",
  scanHistoryCount: "{count} of {limit} scans",
  scanHistoryHint:
    "Newest first. Stored in this browser for your user — nothing is sent to the server.",
  scanHistoryEmpty:
    "No scans yet. Every code you scan lands here — labels, product barcodes, URLs, Wi-Fi and text.",
  scanHistoryClear: "Clear history",
  scanHistoryClearTitle: "Clear scan history?",
  scanHistoryClearBody:
    "This removes every stored scan for this user in this browser. It cannot be undone.",
  scanHistoryLoad: "Load",
  scanHistoryUnknownLabel: "Unknown label",
  scanHistoryUnavailable: "Target no longer exists",
  scanHistoryOffline: "Offline — not in the local cache",
  scanHistoryTrashed: "In the trash",
  scanHistoryGlyphUrl: "URL",
  scanHistoryGlyphWifi: "WIFI",
  scanHistoryGlyphText: "TXT",
  scanHistoryPayloadUrl: "URL",
  scanHistoryPayloadWifi: "Wi-Fi · {ssid}",
  scanHistoryPayloadWifiGeneric: "Wi-Fi network",
  scanHistoryPayloadText: "Text",
  scanHistoryJustNow: "just now",
  scanHistoryMinutesAgo: "{count} min ago",
  scanHistoryHoursAgo: "{count} h ago",
  scanHistoryDaysAgo: "{count} d ago",
  barcodeTypeQr: "QR",
  scanTrashNotice: "This container is in the trash.",
  export: "Export",
  printBlank: "Blank labels",
  printBlankCount: "Labels",
  printKindFilter: "Filter by kind",
  printFilterAll: "All",
  printFilterBoxes: "Boxes",
  printFilterNonBoxes: "Non-boxes",
  printShowing: "Showing {start}–{end} of {total}",
  printLayout: "Layout",
  printLayoutStack: "Stack",
  printLayoutRow: "Row",
  printLayoutSplit: "Split",
  printShowIcons: "Icons",
  printShowQr: "QR",
  printShowShortId: "Short ID",
  printMarginMm: "Margin (mm)",
  printMarginMode: "Margin mode",
  printMarginModeUniform: "Uniform",
  printMarginModeXy: "X / Y",
  printMarginModeEdges: "Per edge",
  printMarginX: "Margin X (mm)",
  printMarginY: "Margin Y (mm)",
  printMarginTop: "Top (mm)",
  printMarginRight: "Right (mm)",
  printMarginBottom: "Bottom (mm)",
  printMarginLeft: "Left (mm)",
  printGapMm: "Gap (mm)",
  printCustomizeSpacing: "Customize spacing",
  printLegibilityBreach: "QR code very small (<6 mm) — may be hard to scan on some laser prints",
  createByScan: "New container from scanned label {id}",
  createConflict: "This label already exists — open the container instead?",
  openInstead: "Open existing container",
  scanOpenContainer: "Open {name}",
  openPhoto: "Open photo",
  lightboxZoom: "Zoom",
  lightboxZoomIn: "Zoom in",
  lightboxZoomOut: "Zoom out",
  lightboxPrev: "Previous photo",
  lightboxNext: "Next photo",
  mediaReplayHint: "Memos replay with the built-in controls.",
  preview: "Preview",
  gallery: "Photos",
  galleryEmpty: "No photos yet — add them on a container's detail page.",
  loadError: "Could not load — is the server reachable?",
  copyUuid: "Copy UUID",
  uuidCopied: "UUID copied to clipboard.",
  labelPreview: "Label",
  showLabelPreview: "Show label",
  // AI summary (Phase 2 / ticket 06)
  aiSummaryTitle: "AI summary",
  aiSummaryByAi: "by AI",
  aiSummaryBlock: "AI summary block",
  aiSummaryFieldDescription: "AI summary for description",
  aiSummaryFieldOwner: "AI summary for owner",
  aiSummaryGeneratedAt: "Generated {date}",
  aiSummaryModel: "Model: {model}",
  aiSummarySources: "Sources",
  aiSummarySourceDescription: "description",
  aiSummarySourceTranscript: "transcript",
  aiSummarySourceDetection: "detection",
  aiSummaryTranscript: "Transcript",
  aiSummaryMergedData: "Merged data",
  aiSummaryDataOwner: "Owner: {owner}",
  aiSummaryReExtract: "Re-extract",
  aiSummaryReExtractLabel: "Re-extract AI summary",
  aiSummaryReExtracting: "Re-extracting…",
  aiSummaryReExtractQueued: "AI re-extract started; the new version appears when the merge finishes.",
  aiSummaryReExtractFailed: "AI re-extract failed",
  // Re-trigger (ticket 09)
  reTranscribe: "Re-transcribe",
  reTranscribed: "Re-transcribed",
  reTranscribeFailed: "Re-transcribe failed",
  showTranscript: "Show transcript",
  hideTranscript: "Hide transcript",
  rePreview: "Re-generate preview",
  rePreviewed: "Preview re-generated",
  rePreviewFailed: "Re-preview failed",
  // Manual transcript override (ADR-0035, manual-stt-edit 03)
  manualTranscript: "manual",
  editTranscript: "Edit transcript",
  editTranscriptTitle: "Edit transcript",
  transcriptField: "Transcript",
  transcriptEmptyHint: "Leave empty to mark this memo as having no transcript.",
  originalTranscript: "Original transcript",
  showOriginalTranscript: "Show original",
  hideOriginalTranscript: "Hide original",
  copyTranscript: "Copy original",
  transcriptCopied: "Transcript copied.",
  transcriptCopyFailed: "Could not copy the transcript.",
  transcriptSaved: "Transcript saved.",
  transcriptSaveFailed: "Could not save the transcript.",
  resetTranscript: "Reset to auto transcript",
  transcriptReset: "Reset to the auto transcript.",
  transcriptResetFailed: "Could not reset the transcript.",
  items: "Items",
  itemsEmpty: "No items yet — scan a barcode to add one.",
  scanBarcode: "Scan barcode",
  incrementQuantity: "Increase quantity",
  decrementQuantity: "Decrease quantity",
  removeItem: "Remove item",
  scanToAddTitle: "Scan to Add",
  scanToAddBarcodePlaceholder: "Or enter barcode manually",
  scanToAddLookup: "Look up",
  scanToAddConfirm: "Add to container",
  scanToAddUnknown: "Barcode {barcode} not found — enter details below.",
  scanToAddNameLabel: "Item name",
  scanToAddLookupFailed: "Lookup failed — try again.",
  scanToAddFailed: "Failed to add item.",
  scanToAddAlreadyHere: "Already in this container: {count}",
  scanToAddNewTotal: "New total in this container",
  scanToAddAdded: "Added {name}",
  scanToAddDone: "Done",
  createItemTitle: "Add item",
  createItemConfirm: "Create item",
  createItemFromLabelTitle: "New item from label {id}",
  // Global scan quantity block (results-03: immediate write, one-level undo)
  scanAdjustChoosePlacement: "Choose a placement",
  scanAdjustContainer: "Container",
  scanAdjustImmediate: "Updates immediately",
  scanAdjustSaved: "Count updated: {name}",
  scanAdjustFailed: "Failed to update the count.",
  scanAdjustFailedReason: "Failed to update the count: {reason}",
  offlineNotSaved: "Offline — not saved.",
  category_book: "Book",
  category_food: "Food",
  category_device: "Device",
  category_media: "Media",
  category_other: "Other",
  itemsList: "Items",
  itemListEmpty: "No items yet — scan a barcode to add one.",
  itemDetail: "Item detail",
  itemPhotos: "Photos",
  itemAddPhoto: "Add photo",
  itemAddPhotoFailed: "Failed to add a photo.",
  itemRemovePhoto: "Remove photo",
  itemPhotoMoveLeft: "Move photo left",
  itemPhotoMoveRight: "Move photo right",
  itemPhotoSetCover: "Set as cover",
  itemPhotosReordered: "Photo order saved",
  itemReorderPhotoFailed: "Could not reorder photos.",
  itemNutrition: "Nutrition",
  itemContainers: "Containers",
  itemRefresh: "Refresh",
  itemDelete: "Delete item",
  itemDeleteError: "Could not delete the item: {error}",
  stagedPhotoHint: "Add photos with the camera or from files — they are attached when the item is saved.",
  stagedPhotoCount: "{count} photos staged for this item",
  stagedPhotoCountOne: "{count} photo staged for this item",
  itemDeleteConfirmTitle: "Delete this item?",
  itemDeleteConfirmBody: "This item will be permanently deleted.",
  itemMetadata: "Metadata",
  itemEdit: "Edit",
  itemEditDialogTitle: "Edit item details",
  itemEditName: "Name",
  itemEditDescription: "Description",
  itemEditKey: "Key",
  itemEditValue: "Value",
  itemEditAddRow: "Add row",
  itemEditRemoveRow: "Remove row",
  itemEditSaved: "Item saved",
  itemEditSaveError: "Could not save the item.",
  itemNoContainers: "Not in any container",
  itemNoPhotos: "No photos yet — add one with the camera or from files.",
  sortAlpha: "A–Z",
  sortNewest: "Newest",
  sortCategory: "Type",
  sortSoonest: "Soonest",
  // Deadlines (deadlines 02, ADR-0037)
  deadlines: "Deadlines",
  deadlineColumn: "Deadline",
  deadlineMore: "+{count} more",
  deadlineNoneDue: "No deadlines due",
  deadlineOverdue: "Overdue",
  deadlineSoon: "Expiring soon",
  deadlineOverdueCount: "{count} overdue",
  deadlineSoonCount: "{count} soon",
  deadlineFilters: "Deadline filters",
  deadlineLabel: "Deadline",
  deadlineFilterEmpty: "Nothing matches these deadline filters.",
  deadlineSortBySoonest: "Sort by soonest deadline",
  deadlineSortByName: "Sorted by soonest deadline; switch to name order",
  deadlineToday: "today",
  deadlineYesterday: "yesterday",
  deadlineInvalidDate: "Enter a valid date (YYYY-MM-DD).",
  deadlineBestBefore: "Best before",
  deadlineReturnBy: "Return by",
  deadlineExpiresOn: "Expires on",
  deadlineWarrantyUntil: "Warranty until",
  deadlinePurchasedOn: "Purchased on",
  clearDate: "Clear date",
  itemDates: "Dates",
  bulkEdit: "Select",
  bulkRefresh: "Refresh ({count})",
  bulkDelete: "Delete ({count})",
  selectItems: "Select items",
  cancelSelection: "Cancel",
  cancelSelectionMode: "Cancel selection",
  open: "Open",
  upOneLevel: "Up one level",
  destinationPath: "Destination path",
  drillInto: "Open {name}",
  refreshProgress: "Refreshing…",
  bulkDeleteConfirmTitle: "Delete selected items?",
  bulkDeleteConfirmBody: "{count} items will be permanently deleted.",
  bulkDeleteConfirmBodyOne: "{count} item will be permanently deleted.",
  searchPlaceholderItems: "Search items…",
  themeLight: "Light",
  themeDark: "Dark",
  themeSystem: "System",
  profile: "Profile",
  // Catalog entries + sharing (ticket 07)
  catalog: "Catalog",
  catalogs: "Catalogs",
  currentCatalog: "Current catalog",
  manage: "Manage",
  defaultCatalogHint: "New top-level containers go to the default catalog.",
  myCatalogs: "My catalogs",
  sharedWithMe: "Shared with me",
  sharedFrom: "by {owner}",
  sharedBy: "Shared by {owner}",
  newEntry: "New catalog",
  entryName: "Catalog name",
  createEntryTitle: "Create a catalog entry",
  create: "Create",
  entryEmpty: "No catalogs yet — create your first catalog entry.",
  defaultEntry: "Default",
  entryRename: "Rename",
  entrySetDefault: "Set as default",
  entryDelete: "Delete catalog",
  entryShare: "Share…",
  deleteEntryConfirmTitle: "Delete catalog “{name}”?",
  deleteEntryConfirmBody: "Type the catalog name to confirm. All containers reachable from it move to your trash.",
  shareTitle: "Share catalog",
  sharees: "Shared with",
  shareesEmpty: "Nobody yet — share this catalog with another user.",
  revoke: "Revoke",
  levelOwner: "Owner",
  levelAdmin: "Admin",
  levelWrite: "Write",
  levelRead: "Read",
  catalogAssignment: "Catalog",
  formEntry: "Catalog entry",
  createEntryFallback: "No write access — using {entry}",
  sidebarItems: "Items",
  galleryFilterAll: "All",
  gallerySource: "Source",
  gallerySourceContainers: "Container photos",
  gallerySourceItems: "Item photos",
  galleryFilterGeneral: "General",
  galleryFilterNutrition: "Nutrition",
  goToContainer: "Go to container",
  goToItem: "Go to item",
  // Gallery image search (ticket 09)
  galleryMode: "Gallery mode",
  galleryModeBrowse: "All photos",
  galleryModeAppearance: "By appearance",
  appearancePlaceholder: "Describe a photo…",
  appearanceLoading: "Searching…",
  appearanceDownloading: "Downloading search model…",
  appearanceOffline: "Image search needs to download its model once while online.",
  appearanceError: "Image search failed — is the server reachable?",
  appearanceNoMatches: "No matching photos — try a different description.",
  // Search result disclosure (search-result-disclosure 03–05)
  filterOpen: "Open search filter",
  filterClose: "Hide search filter",
  filterScope: "Scope",
  filterOwner: "Owner",
  filterTag: "Tag",
  filterType: "Type",
  filterDisplay: "Display",
  filterShow: "show",
  filterHide: "hide",
  filterClearAll: "Clear all",
  filterSemantics:
    "OR within a row · AND across rows · values and counts respect the other filters",
  filterNoneLeft: "none left",
  filterAny: "Any",
  filterPreviews: "Previews",
  filterDescriptions: "Descriptions",
  filterUnassigned: "Unassigned",
  searchClearFilters: "Clear filters",
  searchUnplaced: "not placed in a container",
  searchOpen: "Open",
  searchContents: "What's inside",
  searchPhotoCount: "{count} photos",
  searchPhotoCountOne: "{count} photo",
  searchLess: "Less",
  searchPlacements: "Placements",
  searchInContainers: "In {count} containers",
  openContainer: "Open container",
  undo: "Undo",
  placementRemoved: "{name} removed from this container.",
  searchMoreItems: "+{count} more",
  searchNoPhotos: "No photos",
  relatedPhotos: "Related photos",
} as const;
